/**
 * Klient auth + onboarding spin — tylko zamrożone kontrakty §18.
 * E2E z serwerem: register / login / profile-select / recovery / me / spin.
 */
import {
  LoginRequestSchema,
  LoginResponseSchema,
  MeResponseSchema,
  ProfileSelectRequestSchema,
  ProfileSelectResponseSchema,
  RecoveryRequestSchema,
  RecoveryResponseSchema,
  RegisterRequestSchema,
  RegisterResponseSchema,
  SpinRequestSchema,
  SpinResponseSchema,
  type LoginResponse,
  type MeResponse,
  type ProfileSelectResponse,
  type ProfileSummary,
  type RecoveryResponse,
  type RegisterRequest,
  type RegisterResponse,
  type SpinResponse,
} from '@pieskowo/shared';
import { apiBase } from '../../core/apiBase.js';
import { SaveLocal } from '../../core/SaveLocal.js';

export type ApiFailure = {
  ok: false;
  code: string;
  status: number;
  offline?: boolean;
};

export type ApiOk<T> = { ok: true; data: T };
export type ApiResult<T> = ApiOk<T> | ApiFailure;

async function postJson<T>(
  path: string,
  body: unknown,
  parse: (json: unknown) => T | null,
  auth = false,
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (auth) {
    const token = SaveLocal.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  try {
    const res = await fetch(`${apiBase()}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      try {
        const errJson = (await res.json()) as { error?: { code?: string } };
        if (errJson?.error?.code) code = errJson.error.code;
      } catch {
        /* ignore */
      }
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const data = parse(json);
    if (data === null) {
      return { ok: false, code: 'VALIDATION', status: res.status };
    }
    return { ok: true, data };
  } catch {
    return { ok: false, code: 'OFFLINE', status: 0, offline: true };
  }
}

async function getJson<T>(
  path: string,
  parse: (json: unknown) => T | null,
): Promise<ApiResult<T>> {
  const token = SaveLocal.getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const res = await fetch(`${apiBase()}${path}`, { method: 'GET', headers });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      try {
        const errJson = (await res.json()) as { error?: { code?: string } };
        if (errJson?.error?.code) code = errJson.error.code;
      } catch {
        /* ignore */
      }
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const data = parse(json);
    if (data === null) {
      return { ok: false, code: 'VALIDATION', status: res.status };
    }
    return { ok: true, data };
  } catch {
    return { ok: false, code: 'OFFLINE', status: 0, offline: true };
  }
}

export async function registerAccount(
  input: RegisterRequest,
): Promise<ApiResult<RegisterResponse>> {
  const req = RegisterRequestSchema.safeParse(input);
  if (!req.success) {
    return { ok: false, code: 'VALIDATION', status: 400 };
  }
  return postJson('/api/auth/register', req.data, (json) => {
    const p = RegisterResponseSchema.safeParse(json);
    return p.success ? p.data : null;
  });
}

export async function loginAccount(input: {
  accountId?: string;
  parentCredential: string;
}): Promise<ApiResult<LoginResponse>> {
  const req = LoginRequestSchema.safeParse(input);
  if (!req.success) {
    return { ok: false, code: 'VALIDATION', status: 400 };
  }
  return postJson('/api/auth/login', req.data, (json) => {
    const p = LoginResponseSchema.safeParse(json);
    return p.success ? p.data : null;
  });
}

/** POST /api/auth/profile-select — wiąże aktywny profil i rotuje token. */
export async function selectProfileApi(
  profileId: string,
): Promise<ApiResult<ProfileSelectResponse>> {
  const req = ProfileSelectRequestSchema.safeParse({ profileId });
  if (!req.success) {
    return { ok: false, code: 'VALIDATION', status: 400 };
  }
  return postJson(
    '/api/auth/profile-select',
    req.data,
    (json) => {
      const p = ProfileSelectResponseSchema.safeParse(json);
      return p.success ? p.data : null;
    },
    true,
  );
}

/** POST /api/auth/recovery — kod odzyskania + nowy PIN opiekuna. */
export async function recoverAccountApi(input: {
  recoveryCode: string;
  newParentCredential: string;
}): Promise<ApiResult<RecoveryResponse>> {
  const req = RecoveryRequestSchema.safeParse(input);
  if (!req.success) {
    return { ok: false, code: 'VALIDATION', status: 400 };
  }
  return postJson('/api/auth/recovery', req.data, (json) => {
    const p = RecoveryResponseSchema.safeParse(json);
    return p.success ? p.data : null;
  });
}

export async function fetchMe(): Promise<ApiResult<MeResponse>> {
  return getJson('/api/me', (json) => {
    const p = MeResponseSchema.safeParse(json);
    return p.success ? p.data : null;
  });
}

export async function spinFortuneApi(idempotencyKey: string): Promise<ApiResult<SpinResponse>> {
  const req = SpinRequestSchema.safeParse({ idempotencyKey });
  if (!req.success) {
    return { ok: false, code: 'VALIDATION', status: 400 };
  }
  return postJson(
    '/api/onboarding/spin',
    req.data,
    (json) => {
      const p = SpinResponseSchema.safeParse(json);
      return p.success ? p.data : null;
    },
    true,
  );
}

export function persistAuth(data: {
  token: string;
  accountId: string;
  profileId: string;
}): void {
  SaveLocal.setToken(data.token);
  SaveLocal.setAccountId(data.accountId);
  SaveLocal.setProfileId(data.profileId);
}

/**
 * Po loginie: jeden profil / autoSelected → id do wybrania;
 * wiele profili bez auto → null (UI wyboru).
 */
export function resolveLoginProfileId(login: LoginResponse): string | null {
  if (login.autoSelectedProfileId) {
    const found = login.profiles.find((p) => p.profileId === login.autoSelectedProfileId);
    if (found) return found.profileId;
  }
  if (login.profiles.length === 1) {
    return login.profiles[0]!.profileId;
  }
  return null;
}

/** Walidacja PIN opiekuna (4–8 cyfr) — czysta funkcja pod UI i testy. */
export function isValidParentPin(pin: string): boolean {
  return /^\d{4,8}$/.test(pin);
}

export type ProfileDraft = {
  dogName: string;
  breed: string;
  color: string;
  characterKind: 'dog' | 'dragon';
  offline?: boolean;
};

const DRAFT_KEY = 'pieskowo.onboardingDraft';

/**
 * Draft NIGDY nie persistuje PIN opiekuna (finding P1-7). Nawet jeśli wołający
 * przekaże pole z credentialem, jest ono usuwane przed zapisem do localStorage.
 */
export function saveProfileDraft(draft: ProfileDraft): void {
  const raw = draft as ProfileDraft & Record<string, unknown>;
  const safe: ProfileDraft = {
    dogName: raw.dogName,
    breed: raw.breed,
    color: raw.color,
    characterKind: raw.characterKind,
  };
  if (raw.offline !== undefined) safe.offline = raw.offline as boolean;
  SaveLocal.setJson(DRAFT_KEY, safe);
}

export function loadProfileDraft(): ProfileDraft | null {
  const d = SaveLocal.getJson<ProfileDraft | null>(DRAFT_KEY, null);
  return d;
}

export type { ProfileSummary };
